import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { campaignAPI, examSetAPI, sessionAPI, codingQuestionAPI } from '../../services/api';
import { CODING_LANGUAGE_OPTIONS, codingLanguageLabel, isReviewLanguage } from '../../utils/codingLanguages';
import { getPublicApiOrigin } from '../../config/env';
import { examModeAllowed } from '../../utils/entitlements';
import PermissionDeniedPanel from '../PermissionDeniedPanel';
import GroupedQuestionList, { C, groupQuestions, filterPoolByExamType } from './GroupedQuestionList';
import AddCandidatesLauncher from '../../pages/position/AddCandidatesLauncher';
import ReportModal from '../../pages/position/ReportModal';

function Icon({ children, size = 14, color = 'currentColor', strokeWidth = 1.8 }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} width={size} height={size} strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flexShrink: 0 }}>
      {children}
    </svg>
  );
}
const BriefcaseIcon = (p) => <Icon {...p}><rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></Icon>;
const GlobeIcon = (p) => <Icon {...p}><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></Icon>;
const LayersIcon = (p) => <Icon {...p}><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></Icon>;
const FileTextIcon = (p) => <Icon {...p}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></Icon>;
const UsersIcon = (p) => <Icon {...p}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></Icon>;
const CheckCircleIcon = (p) => <Icon {...p}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 17.01" /></Icon>;
const ArrowRightIcon = (p) => <Icon {...p}><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></Icon>;
const MailIcon = (p) => <Icon {...p}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></Icon>;
const BarChartIcon = (p) => <Icon {...p}><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></Icon>;
const XIcon = (p) => <Icon {...p}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></Icon>;
const CodeIcon = (p) => <Icon {...p}><polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" /></Icon>;
const PlusIcon = (p) => <Icon {...p}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></Icon>;
const MoreIcon = (p) => <Icon {...p}><circle cx="12" cy="5" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="12" cy="19" r="1" /></Icon>;

const STATUS_STYLE = {
  completed: { bg: '#ECFDF5', color: '#065F46', label: 'Completed' },
  started: { bg: '#FEF3C7', color: '#92400E', label: 'In Progress' },
  invited: { bg: '#EFF6FF', color: '#1D4ED8', label: 'Invited' },
  pending: { bg: '#F3F4F6', color: '#6B7280', label: 'Pending' },
};

export async function sendSingleInvite(candidateId) {
  const base = getPublicApiOrigin();
  const token = localStorage.getItem('token');
  const res = await fetch(`${base}/api/candidates/${candidateId}/send-invite`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) throw new Error(data.detail || 'Failed to send invite');
  return data;
}

export async function sendBulkInvite(candidateIds) {
  const base = getPublicApiOrigin();
  const token = localStorage.getItem('token');
  const res = await fetch(`${base}/api/candidates/bulk-invite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ candidate_ids: candidateIds }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) throw new Error(data.detail || 'Bulk invite failed');
  return data;
}

export const EXAM_MODES = [
  {
    id: 'jd',
    title: 'JD-Specific Exam',
    desc: 'Technical & behavioral questions generated from the job description.',
    Icon: BriefcaseIcon,
  },
  {
    id: 'english',
    title: 'Language-Proficiency Exam',
    desc: 'Grammar, vocabulary, reading & spoken-fluency questions contextualized to this role.',
    Icon: GlobeIcon,
  },
  {
    id: 'interview_coding',
    title: 'AI + Coding',
    desc: 'AI interview questions first, then a coding round in the same session.',
    Icon: LayersIcon,
  },
  {
    id: 'coding',
    title: 'Coding Exam',
    desc: 'Live code editor with automated test-case scoring — no video, no audio Q&A.',
    Icon: CodeIcon,
  },
];

/** Legacy exam sets created before AI + Coding replaced JD + Language Combined. */
const LEGACY_EXAM_MODES = {
  combined: {
    id: 'combined',
    title: 'JD + Language Combined',
    Icon: LayersIcon,
  },
};

const EXAM_MODE_BADGE_STYLE = {
  jd: { bg: C.brandLight, color: C.brandDark },
  english: { bg: '#ECFEFF', color: '#0E7490' },
  interview_coding: { bg: C.brandLight, color: C.brandDark },
  coding: { bg: '#ECFDF5', color: '#047857' },
  combined: { bg: C.brandLight, color: C.brandDark },
};

function ModeBadge({ examType }) {
  const m = EXAM_MODES.find((x) => x.id === examType) || LEGACY_EXAM_MODES[examType] || EXAM_MODES[0];
  const style = EXAM_MODE_BADGE_STYLE[examType] || EXAM_MODE_BADGE_STYLE.jd;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, padding: '4px 10px 4px 8px', borderRadius: 6, background: style.bg, color: style.color }}>
      <m.Icon size={11} strokeWidth={2} color={style.color} />
      {m.title}
    </span>
  );
}

function defaultSelectedKeys(pool, examType) {
  const { withIdx } = groupQuestions(filterPoolByExamType(pool, examType));
  return new Set(withIdx.map((q) => q._key));
}

function defaultAssessmentConfig(campaign) {
  return {
    num_questions: campaign?.questions_count || 3,
    grading_leniency: campaign?.grading_leniency ?? 5,
    min_hire_score: campaign?.min_hire_score ?? 5,
    difficulty: campaign?.difficulty_level || 'medium',
    question_type: campaign?.question_type || 'mixed',
    language: campaign?.language || 'en',
  };
}

const sliderBoxStyle = (accent = C.brand, compact = false) => ({
  width: compact ? 40 : 46,
  height: compact ? 30 : 36,
  borderRadius: compact ? 6 : 8,
  background: C.brandLight,
  border: `1px solid ${accent}`,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: compact ? 13 : 15, fontWeight: 700, color: accent, flexShrink: 0,
});

function ConfigSlider({ label, hint, min, max, step = 1, value, onChange, displayValue, accentColor = C.brand, compact = false }) {
  return (
    <div style={{ marginBottom: compact ? 12 : 18 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: C.t2, display: 'block', marginBottom: compact ? 6 : 8 }}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: compact ? 10 : 14 }}>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={onChange}
          style={{ flex: 1, accentColor: accentColor }}
        />
        <div style={sliderBoxStyle(accentColor, compact)}>{displayValue}</div>
      </div>
      {hint && <div style={{ fontSize: 11, color: C.t4, marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

function ConfigStrictnessSlider({ gradingLeniency, onChange, accentColor = C.brand, compact = false }) {
  return (
    <div style={{ marginBottom: compact ? 12 : 18 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: C.t2, display: 'block', marginBottom: compact ? 6 : 8 }}>Grading Strictness</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: compact ? 10 : 14 }}>
        <span style={{ fontSize: 11, color: C.t4, flexShrink: 0 }}>Lenient</span>
        <input
          type="range"
          min={0}
          max={10}
          step={1}
          value={10 - gradingLeniency}
          onChange={(e) => onChange(10 - parseInt(e.target.value, 10))}
          style={{ flex: 1, accentColor: accentColor }}
        />
        <span style={{ fontSize: 11, color: C.t4, flexShrink: 0 }}>Strict</span>
        <div style={sliderBoxStyle(accentColor, compact)}>{10 - gradingLeniency}</div>
      </div>
      <div style={{ fontSize: 11, color: C.t4, marginTop: 3 }}>How generously answers are scored. Does not change question difficulty.</div>
    </div>
  );
}

/** Assessment config panel (matches position creation wizard step 2). */
function ExamAssessmentConfigPanel({ examType, config, onChange, compact = false }) {
  const set = (key) => (val) => onChange((prev) => ({ ...prev, [key]: val }));
  const panelPad = compact ? 12 : 16;
  const panelMb = compact ? 12 : 18;
  const selectStyle = {
    width: '100%', border: `1px solid ${C.border}`, borderRadius: compact ? 6 : 8,
    padding: compact ? '7px 10px' : '9px 12px', fontSize: 12, color: C.t1,
    background: '#fff', cursor: 'pointer', fontFamily: 'inherit', boxSizing: 'border-box',
  };

  if (examType === 'coding') {
    return (
      <div style={{ marginBottom: panelMb, padding: panelPad, background: '#F8FAFC', border: `1px solid ${C.borderLight}`, borderRadius: 8 }}>
        <ConfigSlider
          label="Number of problems"
          min={1}
          max={5}
          value={config.num_questions}
          onChange={(e) => set('num_questions')(parseInt(e.target.value, 10))}
          displayValue={config.num_questions}
          accentColor="#0D9488"
          compact={compact}
        />
        <ConfigStrictnessSlider
          gradingLeniency={config.grading_leniency}
          onChange={set('grading_leniency')}
          accentColor="#0D9488"
          compact={compact}
        />
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: C.t2, display: 'block', marginBottom: 6 }}>Difficulty Level</label>
          <select value={config.difficulty} onChange={(e) => set('difficulty')(e.target.value)} style={selectStyle}>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>
        </div>
      </div>
    );
  }

  if (examType === 'english') {
    return (
      <div style={{ marginBottom: panelMb, padding: panelPad, background: '#F8FAFC', border: `1px solid ${C.borderLight}`, borderRadius: 8 }}>
        <ConfigSlider
          label="Number of Questions"
          hint="Grammar, vocabulary, reading and spoken fluency."
          min={4}
          max={12}
          value={config.num_questions}
          onChange={(e) => set('num_questions')(parseInt(e.target.value, 10))}
          displayValue={config.num_questions}
          accentColor="#DB2777"
          compact={compact}
        />
        <ConfigStrictnessSlider gradingLeniency={config.grading_leniency} onChange={set('grading_leniency')} accentColor="#DB2777" compact={compact} />
        <ConfigSlider
          label="Minimum Hire Score"
          hint="Candidates scoring at or above this overall score are labeled Hire."
          min={0}
          max={10}
          step={0.5}
          value={config.min_hire_score}
          onChange={(e) => set('min_hire_score')(parseFloat(e.target.value))}
          displayValue={Number(config.min_hire_score).toFixed(1)}
          accentColor="#DB2777"
          compact={compact}
        />
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: C.t2, display: 'block', marginBottom: 6 }}>Difficulty Level</label>
          <select value={config.difficulty} onChange={(e) => set('difficulty')(e.target.value)} style={selectStyle}>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>
        </div>
      </div>
    );
  }

  const showInterviewConfig = examType === 'jd' || examType === 'interview_coding';
  if (!showInterviewConfig) return null;

  return (
    <div style={{ marginBottom: panelMb, padding: panelPad, background: '#F8FAFC', border: `1px solid ${C.borderLight}`, borderRadius: 8 }}>
      {examType === 'interview_coding' && (
        <div style={{ fontSize: 12, fontWeight: 700, color: C.t1, marginBottom: compact ? 10 : 14 }}>AI Interview</div>
      )}
      <ConfigSlider
        label="Number of Questions"
        hint="Default: 3 questions"
        min={1}
        max={10}
        value={config.num_questions}
        onChange={(e) => set('num_questions')(parseInt(e.target.value, 10))}
        displayValue={config.num_questions}
        compact={compact}
      />
      <ConfigStrictnessSlider gradingLeniency={config.grading_leniency} onChange={set('grading_leniency')} compact={compact} />
      <ConfigSlider
        label="Minimum Hire Score"
        hint="Candidates scoring at or above this overall score are labeled Hire."
        min={0}
        max={10}
        step={0.5}
        value={config.min_hire_score}
        onChange={(e) => set('min_hire_score')(parseFloat(e.target.value))}
        displayValue={Number(config.min_hire_score).toFixed(1)}
        compact={compact}
      />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: compact ? 10 : 14 }}>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: C.t2, display: 'block', marginBottom: 6 }}>Difficulty Level</label>
          <select value={config.difficulty} onChange={(e) => set('difficulty')(e.target.value)} style={selectStyle}>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
            <option value="adaptive">Adaptive</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: C.t2, display: 'block', marginBottom: 6 }}>Question Type</label>
          <select value={config.question_type} onChange={(e) => set('question_type')(e.target.value)} style={selectStyle}>
            <option value="mixed">Mixed</option>
            <option value="technical">Technical Only</option>
            <option value="behavioral">Behavioral Only</option>
          </select>
        </div>
      </div>
    </div>
  );
}

function mapSavedQuestionRow(q) {
  return {
    id: q.id,
    question: q.question || q.question_text || '',
    type: (q.type || q.question_type || 'technical').toLowerCase(),
    difficulty: (q.difficulty || 'medium').toLowerCase(),
    exam_category: q.exam_category || 'jd',
    skill_area: q.skill_area,
    language: q.language,
  };
}

const inputStyle = { width: '100%', fontSize: 13, padding: '8px 10px', border: `1px solid ${C.border}`, borderRadius: 6, fontFamily: 'inherit', boxSizing: 'border-box' };

const EMPTY_CODING_FORM = {
  title: '', description: '', starterCode: '', questionType: 'python',
  visibleInput: '', visibleExpected: '', hiddenInput: '', hiddenExpected: '',
  visibleInput2: '', visibleExpected2: '', hiddenInput2: '', hiddenExpected2: '',
  hiddenInput3: '', hiddenExpected3: '', hiddenInput4: '', hiddenExpected4: '',
};

/** AI-generates coding problems into the campaign bank (same call as before). */
export async function generateCodingProblems(campaign, generateType = 'python', generateOptions = null) {
  const opts = generateOptions || {};
  const languages = (Array.isArray(opts.languages) ? opts.languages : [])
    .map((item) => String(item || '').trim())
    .filter(Boolean);
  const selected = languages.length ? languages : [generateType];
  const n = Math.min(5, Math.max(1, parseInt(opts.num_questions ?? campaign.questions_count ?? campaign.num_questions, 10) || 3));
  const difficulty = opts.difficulty || campaign.difficulty_level || 'medium';
  const sqlOnly = selected.length === 1 && selected[0] === 'sql';
  await codingQuestionAPI.generate(campaign.id, {
    num_questions: n,
    difficulty: difficulty === 'adaptive' ? 'medium' : difficulty,
    grading_leniency:
      opts.grading_leniency != null
        ? opts.grading_leniency
        : (campaign.grading_leniency != null ? campaign.grading_leniency : undefined),
    question_type: sqlOnly ? 'sql' : 'classic',
    language: sqlOnly ? undefined : selected[0],
    languages: sqlOnly ? ['sql'] : selected,
  });
}

/** Read-only detail of one coding question (statement, tests, starter code). */
export function CodingQuestionDetailModal({ campaign, question, onClose }) {
  const [detail, setDetail] = useState({ id: question.id, title: question.title });
  const [detailLoading, setDetailLoading] = useState(true);
  const [detailError, setDetailError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setDetailError('');
    setDetailLoading(true);
    setDetail({ id: question.id, title: question.title });
    codingQuestionAPI.get(campaign.id, question.id)
      .then((res) => { if (!cancelled) setDetail(res.data); })
      .catch((err) => { if (!cancelled) setDetailError(err.response?.data?.detail || 'Failed to load question details.'); })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [campaign.id, question.id, question.title]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const closeDetail = onClose;
  const visibleCases = (detail?.test_cases || []).filter((tc) => tc.is_visible);
  const hiddenCases = (detail?.test_cases || []).filter((tc) => !tc.is_visible);
  const starterEntries = Object.entries(detail?.starter_code || {}).filter(([, code]) => code);
  const isSqlQuestion = (detail?.languages || []).includes('sql');

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={closeDetail}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15,23,42,.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(720px, 100%)', maxHeight: '88vh', overflow: 'auto',
          background: '#fff', borderRadius: 12, boxShadow: '0 20px 50px rgba(15,23,42,.25)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
          padding: '16px 18px', borderBottom: `1px solid ${C.borderLight}`, position: 'sticky', top: 0, background: '#fff', zIndex: 1,
        }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#4338CA', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 4 }}>
              Coding Question
            </div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.t1 }}>{detail.title || 'Question'}</h3>
          </div>
          <button type="button" onClick={closeDetail}
            style={{ background: 'none', border: 'none', color: C.t4, cursor: 'pointer', padding: 4 }}
            aria-label="Close">
            <XIcon size={16} strokeWidth={2} />
          </button>
        </div>

        <div style={{ padding: 18 }}>
          {detailLoading && (
            <div style={{ fontSize: 13, color: C.t4, padding: '24px 0', textAlign: 'center' }}>Loading details…</div>
          )}
          {detailError && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#991B1B' }}>
              {detailError}
            </div>
          )}
          {!detailLoading && !detailError && detail.description != null && (
            <>
              <section style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 8 }}>Overview</div>
                <pre style={{
                  margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 13, lineHeight: 1.55,
                  color: C.t2, background: '#F8FAFC', border: `1px solid ${C.borderLight}`, borderRadius: 8, padding: 12,
                }}>{detail.description}</pre>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10, fontSize: 12, color: C.t3 }}>
                  <span>Languages: {(detail.languages || []).join(', ') || 'python'}</span>
                  <span>·</span>
                  <span>Time limit: {detail.time_limit_ms} ms</span>
                  <span>·</span>
                  <span>Memory: {detail.memory_limit_kb} KB</span>
                </div>
              </section>

              <section style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 8 }}>
                  Visible test cases ({visibleCases.length})
                </div>
                {visibleCases.length === 0 ? (
                  <div style={{ fontSize: 12, color: C.t4 }}>No visible tests.</div>
                ) : visibleCases.map((tc, i) => (
                  <div key={tc.id || i} style={{ border: `1px solid ${C.borderLight}`, borderRadius: 8, padding: 10, marginBottom: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.t3, marginBottom: 4 }}>Sample {i + 1}</div>
                    <div style={{ fontSize: 12, color: C.t2 }}><strong>{isSqlQuestion ? 'Setup SQL:' : 'Input:'}</strong></div>
                    <pre style={{ margin: '4px 0 8px', fontSize: 12, background: '#F1F5F9', padding: 8, borderRadius: 6, whiteSpace: 'pre-wrap' }}>{tc.input || '(empty)'}</pre>
                    <div style={{ fontSize: 12, color: C.t2 }}><strong>{isSqlQuestion ? 'Expected rows:' : 'Expected:'}</strong></div>
                    <pre style={{ margin: '4px 0 0', fontSize: 12, background: '#F1F5F9', padding: 8, borderRadius: 6, whiteSpace: 'pre-wrap' }}>{tc.expected_output || '(empty)'}</pre>
                  </div>
                ))}
              </section>

              <section style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 8 }}>
                  Hidden test cases ({hiddenCases.length})
                </div>
                {hiddenCases.length === 0 ? (
                  <div style={{ fontSize: 12, color: C.t4 }}>No hidden tests.</div>
                ) : hiddenCases.map((tc, i) => (
                  <div key={tc.id || i} style={{ border: `1px solid ${C.borderLight}`, borderRadius: 8, padding: 10, marginBottom: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.t3, marginBottom: 4 }}>Hidden {i + 1}</div>
                    <div style={{ fontSize: 12, color: C.t2 }}><strong>{isSqlQuestion ? 'Setup SQL:' : 'Input:'}</strong></div>
                    <pre style={{ margin: '4px 0 8px', fontSize: 12, background: '#F1F5F9', padding: 8, borderRadius: 6, whiteSpace: 'pre-wrap' }}>{tc.input || '(empty)'}</pre>
                    <div style={{ fontSize: 12, color: C.t2 }}><strong>{isSqlQuestion ? 'Expected rows:' : 'Expected:'}</strong></div>
                    <pre style={{ margin: '4px 0 0', fontSize: 12, background: '#F1F5F9', padding: 8, borderRadius: 6, whiteSpace: 'pre-wrap' }}>{tc.expected_output || '(empty)'}</pre>
                  </div>
                ))}
              </section>

              <section>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.t3, textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 8 }}>
                  Starter code
                </div>
                {starterEntries.length === 0 ? (
                  <div style={{ fontSize: 12, color: C.t4 }}>No starter code.</div>
                ) : starterEntries.map(([lang, code]) => (
                  <div key={lang} style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#4338CA', marginBottom: 4 }}>{lang}</div>
                    <pre style={{ margin: 0, fontSize: 12, background: '#0F172A', color: '#E2E8F0', padding: 12, borderRadius: 8, overflow: 'auto', whiteSpace: 'pre-wrap' }}>{code}</pre>
                  </div>
                ))}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );

}

/** "+ Add Coding Question" form: statement, starter code, sample + hidden tests,
 * and "Generate supporting assets" (AI test cases). */
export function CodingQuestionForm({ campaign, onSaved, onCancel }) {
  const [form, setForm] = useState(EMPTY_CODING_FORM);
  const [saving, setSaving] = useState(false);
  const [generatingCases, setGeneratingCases] = useState(false);
  const [error, setError] = useState('');
  const updateForm = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const setShowAdd = (v) => { if (!v) onCancel?.(); };

  const handleGenerateCases = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      setError('Title and description are required before generating test cases.');
      return;
    }
    if (isReviewLanguage(form.questionType) || form.questionType === 'sql') return;
    setGeneratingCases(true);
    setError('');
    try {
      const res = await codingQuestionAPI.generateTestCases(campaign.id, {
        title: form.title.trim(),
        description: form.description.trim(),
        language: form.questionType,
        starter_code: form.starterCode || '',
      });
      const samples = res.data?.sample_test_cases || [];
      const hidden = res.data?.hidden_test_cases || [];
      const sample = samples[0] || {};
      const sample2 = samples[1] || {};
      const hiddenCase = hidden[0] || {};
      const hiddenCase2 = hidden[1] || {};
      const hiddenCase3 = hidden[2] || {};
      const hiddenCase4 = hidden[3] || {};
      const textOf = (value) => (value == null ? '' : String(value));
      const addSection = (text, heading, value) => {
        if (!value || text.toUpperCase().includes(heading)) return text;
        return `${text.trim()}\n\n${heading}\n${value}`;
      };
      setForm((prev) => {
        let description = prev.description;
        description = addSection(description, 'INPUT FORMAT', textOf(res.data?.input_format));
        description = addSection(description, 'OUTPUT FORMAT', textOf(res.data?.output_format));
        description = addSection(description, 'CONSTRAINTS', textOf(res.data?.constraints));
        const generatedStarter = textOf(res.data?.starter_code).trim();
        return {
          ...prev,
          description,
          starterCode: prev.starterCode.trim() ? prev.starterCode : (generatedStarter || prev.starterCode),
          visibleInput: textOf(sample.input) || prev.visibleInput,
          visibleExpected: textOf(sample.output) || prev.visibleExpected,
          visibleInput2: textOf(sample2.input) || prev.visibleInput2,
          visibleExpected2: textOf(sample2.output) || prev.visibleExpected2,
          hiddenInput: textOf(hiddenCase.input) || prev.hiddenInput,
          hiddenExpected: textOf(hiddenCase.output) || prev.hiddenExpected,
          hiddenInput2: textOf(hiddenCase2.input) || prev.hiddenInput2,
          hiddenExpected2: textOf(hiddenCase2.output) || prev.hiddenExpected2,
          hiddenInput3: textOf(hiddenCase3.input) || prev.hiddenInput3,
          hiddenExpected3: textOf(hiddenCase3.output) || prev.hiddenExpected3,
          hiddenInput4: textOf(hiddenCase4.input) || prev.hiddenInput4,
          hiddenExpected4: textOf(hiddenCase4.output) || prev.hiddenExpected4,
        };
      });
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to generate test cases.';
      setError(typeof msg === 'string' ? msg : 'Failed to generate test cases.');
    } finally {
      setGeneratingCases(false);
    }
  };

  const handleAdd = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      setError('Title and description are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const testCases = [];
      if (form.visibleInput.trim() || form.visibleExpected.trim()) {
        testCases.push({ input: form.visibleInput, expected_output: form.visibleExpected, is_visible: true });
      }
      if (form.visibleInput2.trim() || form.visibleExpected2.trim()) {
        testCases.push({ input: form.visibleInput2, expected_output: form.visibleExpected2, is_visible: true });
      }
      if (form.hiddenInput.trim() || form.hiddenExpected.trim()) {
        testCases.push({ input: form.hiddenInput, expected_output: form.hiddenExpected, is_visible: false });
      }
      if (form.hiddenInput2.trim() || form.hiddenExpected2.trim()) {
        testCases.push({ input: form.hiddenInput2, expected_output: form.hiddenExpected2, is_visible: false });
      }
      if (form.hiddenInput3.trim() || form.hiddenExpected3.trim()) {
        testCases.push({ input: form.hiddenInput3, expected_output: form.hiddenExpected3, is_visible: false });
      }
      if (form.hiddenInput4.trim() || form.hiddenExpected4.trim()) {
        testCases.push({ input: form.hiddenInput4, expected_output: form.hiddenExpected4, is_visible: false });
      }
      const lang = CODING_LANGUAGE_OPTIONS.some((l) => l.id === form.questionType) ? form.questionType : 'python';
      await codingQuestionAPI.create(campaign.id, {
        title: form.title.trim(),
        description: form.description.trim(),
        languages: [lang],
        starter_code: form.starterCode.trim() ? { [lang]: form.starterCode } : {},
        test_cases: testCases,
      });
      setForm(EMPTY_CODING_FORM);
      onSaved?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create question.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '9px 12px', marginBottom: 12, fontSize: 12, color: '#991B1B' }}>
          {error}
        </div>
      )}
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 14 }}>
          <input style={{ ...inputStyle, marginBottom: 8 }} placeholder="Question title (e.g. Add Two Numbers)" value={form.title} onChange={updateForm('title')} />
          <select value={form.questionType} onChange={(e) => setForm((prev) => ({ ...prev, questionType: e.target.value }))}
            style={{ ...inputStyle, marginBottom: 8 }}>
            {CODING_LANGUAGE_OPTIONS.map((lang) => (
              <option key={lang.id} value={lang.id}>{lang.label}</option>
            ))}
          </select>
          <textarea style={{ ...inputStyle, marginBottom: 8, minHeight: 60 }} placeholder="Description / problem statement" value={form.description} onChange={updateForm('description')} />
          <textarea style={{ ...inputStyle, marginBottom: 8, minHeight: 50, fontFamily: 'monospace' }}
            placeholder={form.questionType === 'sql' ? 'Starter code (SQL — comment only, e.g. -- Write your SELECT query here)' : `Starter code (${codingLanguageLabel(form.questionType)}, optional)`}
            value={form.starterCode} onChange={updateForm('starterCode')} />
          {!isReviewLanguage(form.questionType) && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <input style={inputStyle} placeholder={form.questionType === 'sql' ? 'Sample 1 — Setup SQL' : 'Sample 1 — input'} value={form.visibleInput} onChange={updateForm('visibleInput')} />
                <input style={inputStyle} placeholder={form.questionType === 'sql' ? 'Sample 1 — expected rows' : 'Sample 1 — expected output'} value={form.visibleExpected} onChange={updateForm('visibleExpected')} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <input style={inputStyle} placeholder={form.questionType === 'sql' ? 'Sample 2 — Setup SQL' : 'Sample 2 — input'} value={form.visibleInput2} onChange={updateForm('visibleInput2')} />
                <input style={inputStyle} placeholder={form.questionType === 'sql' ? 'Sample 2 — expected rows' : 'Sample 2 — expected output'} value={form.visibleExpected2} onChange={updateForm('visibleExpected2')} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <input style={inputStyle} placeholder={form.questionType === 'sql' ? 'Hidden 1 — Setup SQL' : 'Hidden 1 — input'} value={form.hiddenInput} onChange={updateForm('hiddenInput')} />
                <input style={inputStyle} placeholder={form.questionType === 'sql' ? 'Hidden 1 — expected rows' : 'Hidden 1 — expected output'} value={form.hiddenExpected} onChange={updateForm('hiddenExpected')} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <input style={inputStyle} placeholder={form.questionType === 'sql' ? 'Hidden 2 — Setup SQL' : 'Hidden 2 — input'} value={form.hiddenInput2} onChange={updateForm('hiddenInput2')} />
                <input style={inputStyle} placeholder={form.questionType === 'sql' ? 'Hidden 2 — expected rows' : 'Hidden 2 — expected output'} value={form.hiddenExpected2} onChange={updateForm('hiddenExpected2')} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <input style={inputStyle} placeholder={form.questionType === 'sql' ? 'Hidden 3 — Setup SQL' : 'Hidden 3 — input'} value={form.hiddenInput3} onChange={updateForm('hiddenInput3')} />
                <input style={inputStyle} placeholder={form.questionType === 'sql' ? 'Hidden 3 — expected rows' : 'Hidden 3 — expected output'} value={form.hiddenExpected3} onChange={updateForm('hiddenExpected3')} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                <input style={inputStyle} placeholder={form.questionType === 'sql' ? 'Hidden 4 — Setup SQL' : 'Hidden 4 — input'} value={form.hiddenInput4} onChange={updateForm('hiddenInput4')} />
                <input style={inputStyle} placeholder={form.questionType === 'sql' ? 'Hidden 4 — expected rows' : 'Hidden 4 — expected output'} value={form.hiddenExpected4} onChange={updateForm('hiddenExpected4')} />
              </div>
            </>
          )}
          {isReviewLanguage(form.questionType) && (
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
              React and Angular questions are reviewed on submit. They do not use sample or hidden tests.
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {!isReviewLanguage(form.questionType) && form.questionType !== 'sql' && (
              <button type="button" onClick={handleGenerateCases} disabled={generatingCases || saving}
                style={{ fontSize: 12, fontWeight: 700, color: '#4338CA', background: '#EEF2FF', border: '1px solid #C7D2FE', padding: '8px 16px', borderRadius: 7, cursor: generatingCases ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
                {generatingCases ? 'Generating…' : 'Generate supporting assets'}
              </button>
            )}
            <button type="button" onClick={handleAdd} disabled={saving}
              style={{ fontSize: 12, fontWeight: 700, color: 'white', background: saving ? C.t4 : C.brand, border: 'none', padding: '8px 16px', borderRadius: 7, cursor: saving ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
              {saving ? 'Saving…' : 'Save Question'}
            </button>
            <button type="button" onClick={() => setShowAdd(false)}
              style={{ fontSize: 12, color: C.t3, background: 'none', border: `1px solid ${C.border}`, padding: '8px 16px', borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit' }}>
              Cancel
            </button>
          </div>
        </div>
    </>
  );
}

/** Manages a campaign's coding-question bank (title/description/starter code
 * + visible/hidden test cases). Coding questions are campaign-scoped, not tied
 * to a specific ExamSet — every coding-mode test on this campaign shares the
 * same bank. Optional AI generate reuses codingQuestionAPI.generate. */
export function CodingQuestionManager({
  campaign,
  onCountChange,
  reloadKey = 0,
  showSectionHeader = false,
  showGenerateButton = false,
  onGenerateError,
  generateOptions = null,
}) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  const [detail, setDetail] = useState(null);
  const [generateType, setGenerateType] = useState('python');

  const load = async () => {
    setLoading(true);
    try {
      const res = await codingQuestionAPI.list(campaign.id);
      const rows = res.data || [];
      setQuestions(rows);
      onCountChange?.(rows.length);
    } catch {
      setQuestions([]);
      onCountChange?.(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [campaign.id, reloadKey]);

  const openDetail = (q) => setDetail({ id: q.id, title: q.title });
  const closeDetail = () => setDetail(null);

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      await generateCodingProblems(campaign, generateType, generateOptions);
      await load();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to generate coding questions.';
      setError(typeof msg === 'string' ? msg : 'Failed to generate coding questions.');
      onGenerateError?.(msg);
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (q, e) => {
    e?.stopPropagation?.();
    if (!window.confirm(`Delete "${q.title}"?`)) return;
    try {
      await codingQuestionAPI.remove(campaign.id, q.id);
      if (detail?.id === q.id) closeDetail();
      load();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Failed to delete question.');
    }
  };

  const detailModal = detail && (
    <CodingQuestionDetailModal campaign={campaign} question={detail} onClose={closeDetail} />
  );

  const body = (
    <div>
      <div style={{ fontSize: 12, color: C.t3, marginBottom: 10 }}>
        {questions.length} coding question(s) in this campaign&apos;s bank — shared by every coding-mode test on this position. Python and SQL are available now (Java/JavaScript require a live Judge0 deployment). Click a question to view details.
      </div>

      {loading ? (
        <div style={{ padding: '16px 0', fontSize: 13, color: C.t4 }}>Loading…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
          {questions.map((q) => (
            <div
              key={q.id}
              role="button"
              tabIndex={0}
              onClick={() => openDetail(q)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDetail(q); } }}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 12px', border: `1px solid ${C.borderLight}`, borderRadius: 8,
                cursor: 'pointer', background: '#fff',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#A5B4FC'; e.currentTarget.style.background = '#F8FAFF'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.borderLight; e.currentTarget.style.background = '#fff'; }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.t1 }}>{q.title}</div>
                <div style={{ fontSize: 11, color: C.t4 }}>{q.visible_test_count} visible · {q.hidden_test_count} hidden test case(s)</div>
              </div>
              <button type="button" onClick={(e) => handleDelete(q, e)}
                style={{ background: 'none', border: 'none', color: C.t4, cursor: 'pointer', padding: 4, flexShrink: 0 }}
                aria-label={`Delete ${q.title}`}>
                <XIcon size={13} strokeWidth={2} />
              </button>
            </div>
          ))}
          {questions.length === 0 && !showAdd && (
            <div style={{ textAlign: 'center', padding: '16px 0', fontSize: 12, color: C.t4, border: `1px dashed ${C.border}`, borderRadius: 8 }}>
              No coding questions yet — generate with AI or add at least one before creating a coding test.
            </div>
          )}
        </div>
      )}

      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '9px 12px', marginBottom: 12, fontSize: 12, color: '#991B1B' }}>
          {error}
        </div>
      )}

      {showAdd ? (
        <CodingQuestionForm campaign={campaign} onSaved={() => { setShowAdd(false); load(); }} onCancel={() => setShowAdd(false)} />
      ) : (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {showGenerateButton && (
            <>
              <select value={generateType} onChange={(e) => setGenerateType(e.target.value)} disabled={generating}
                style={{ fontSize: 12, padding: '7px 8px', borderRadius: 7, border: `1px solid ${C.border}`, fontFamily: 'inherit' }}>
                {CODING_LANGUAGE_OPTIONS.map((lang) => (
                  <option key={lang.id} value={lang.id}>{lang.label}</option>
                ))}
              </select>
              <button type="button" onClick={handleGenerate} disabled={generating}
                style={{ fontSize: 12, fontWeight: 600, color: 'white', background: generating ? C.t4 : '#4F46E5', border: 'none', padding: '8px 14px', borderRadius: 7, cursor: generating ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
                {generating ? 'Generating…' : `⚡ Generate ${codingLanguageLabel(generateType)} Questions`}
              </button>
            </>
          )}
          <button type="button" onClick={() => setShowAdd(true)}
            style={{ fontSize: 12, fontWeight: 600, color: C.brand, background: C.brandLight, border: 'none', padding: '8px 14px', borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit' }}>
            + Add Coding Question
          </button>
        </div>
      )}
      {detailModal}
    </div>
  );

  if (!showSectionHeader) return body;

  return (
    <div style={{ marginTop: 20, marginBottom: 8 }}>
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 12px', background: '#EEF2FF', border: `1px solid #C7D2FE`,
          borderRadius: collapsed ? 10 : '10px 10px 0 0', cursor: 'pointer',
          fontFamily: 'inherit', textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 12, color: '#4338CA' }}>{collapsed ? '▸' : '▾'}</span>
        <span style={{ fontSize: 12, fontWeight: 800, color: '#3730A3', letterSpacing: '.3px', textTransform: 'uppercase' }}>
          Coding Questions ({questions.length})
        </span>
      </button>
      {!collapsed && (
        <div style={{ border: `1px solid #C7D2FE`, borderTop: 'none', borderRadius: '0 0 10px 10px', padding: 14, background: '#fff' }}>
          {body}
        </div>
      )}
    </div>
  );
}

/** The "Set up" content of an exam type: assessment config + question selection
 * (or the coding bank). Used inside ExamTypeSetupModal and the New interview dialog. */
export function ExamSetupBody({
  examType,
  campaign,
  pool,
  config,
  onConfigChange,
  selectedKeys,
  onToggleKey,
  visiblePool,
  error,
  generating,
  creating,
  codingQuestionCount,
  codingGenerateOptions,
  onGenerateJd,
  onGenerateEnglish,
  onCreate,
  onClose,
  onCodingCountChange,
  onCodingGenerateError,
}) {
  const generateJdButton = (examType === 'jd' || examType === 'interview_coding') && (
    <button
      type="button"
      onClick={onGenerateJd}
      disabled={generating}
      style={{ fontSize: 12, fontWeight: 600, color: 'white', background: generating ? C.t4 : C.brand, border: 'none', padding: '8px 14px', borderRadius: 7, cursor: generating ? 'wait' : 'pointer', fontFamily: 'inherit', marginBottom: 12 }}
    >
      {generating ? 'Generating…' : '⚡ Generate from JD'}
    </button>
  );

  const generateEnglishButton = examType === 'english' && (
    <button
      type="button"
      onClick={onGenerateEnglish}
      disabled={generating}
      style={{ fontSize: 12, fontWeight: 600, color: 'white', background: generating ? C.t4 : '#0D9488', border: 'none', padding: '8px 14px', borderRadius: 7, cursor: generating ? 'wait' : 'pointer', fontFamily: 'inherit', marginBottom: 12 }}
    >
      {generating ? 'Generating…' : '⚡ Generate Proficiency Test'}
    </button>
  );

  return (
    <>
      <ExamAssessmentConfigPanel examType={examType} config={config} onChange={onConfigChange} compact />

      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 12, color: '#991B1B' }}>
          {error}
        </div>
      )}

      {examType === 'coding' ? (
        <CodingQuestionManager
          campaign={campaign}
          onCountChange={onCodingCountChange}
          showGenerateButton
          generateOptions={codingGenerateOptions}
          onGenerateError={onCodingGenerateError}
        />
      ) : examType === 'interview_coding' ? (
        <>
          {generateJdButton}
          <div style={{ fontSize: 11, color: C.t3, marginBottom: 8 }}>
            {selectedKeys.size} of {visiblePool.length} AI interview question(s) selected — then add coding problems below.
          </div>
          <div style={{ maxHeight: 180, overflowY: 'auto', border: `1px solid ${C.borderLight}`, borderRadius: 8, padding: 10, marginBottom: 12 }}>
            {pool.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px 0', fontSize: 13, color: C.t4 }}>
                No saved questions yet — use Generate from JD above.
              </div>
            ) : visiblePool.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px 0', fontSize: 13, color: C.t4 }}>
                No AI / JD questions yet — click Generate from JD above.
              </div>
            ) : (
              <GroupedQuestionList
                mode="selectable"
                questions={visiblePool}
                campaign={campaign}
                selectedKeys={selectedKeys}
                onToggleKey={onToggleKey}
              />
            )}
          </div>
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.t1, marginBottom: 10 }}>Coding Round</div>
            <CodingQuestionManager
              campaign={campaign}
              onCountChange={onCodingCountChange}
              showGenerateButton
              generateOptions={codingGenerateOptions}
              onGenerateError={onCodingGenerateError}
            />
          </div>
        </>
      ) : (
        <>
          {generateJdButton}
          {generateEnglishButton}
          <div style={{ fontSize: 11, color: C.t3, marginBottom: 8 }}>
            {selectedKeys.size} of {visiblePool.length} question(s) selected — generate with the settings above, then tick/untick to customize.
          </div>
          <div style={{ maxHeight: 200, overflowY: 'auto', border: `1px solid ${C.borderLight}`, borderRadius: 8, padding: 10, marginBottom: 8 }}>
            {pool.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px 0', fontSize: 13, color: C.t4 }}>
                No saved questions yet — use the generate button above.
              </div>
            ) : visiblePool.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px 0', fontSize: 13, color: C.t4 }}>
                No {examType === 'english' ? 'proficiency' : 'JD'} questions yet — click generate above.
              </div>
            ) : (
              <GroupedQuestionList
                mode="selectable"
                questions={visiblePool}
                campaign={campaign}
                selectedKeys={selectedKeys}
                onToggleKey={onToggleKey}
              />
            )}
          </div>
        </>
      )}
    </>
  );
}

/** Modal: assessment config + question setup for one exam type (opened from type card). */
function ExamTypeSetupModal({
  examType,
  campaign,
  pool,
  config,
  onConfigChange,
  selectedKeys,
  onToggleKey,
  visiblePool,
  error,
  generating,
  creating,
  codingQuestionCount,
  codingGenerateOptions,
  onGenerateJd,
  onGenerateEnglish,
  onCreate,
  onClose,
  onCodingCountChange,
  onCodingGenerateError,
}) {
  const modeMeta = EXAM_MODES.find((m) => m.id === examType) || EXAM_MODES[0];

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(15,23,42,.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(600px, 100%)', maxHeight: '82vh', overflow: 'hidden',
          background: '#fff', borderRadius: 12, boxShadow: '0 16px 40px rgba(15,23,42,.22)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
          padding: '12px 16px', borderBottom: `1px solid ${C.borderLight}`, flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <modeMeta.Icon size={18} color={C.brand} strokeWidth={1.6} />
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.t4, textTransform: 'uppercase', letterSpacing: '.4px' }}>Create Exam</div>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.t1 }}>{modeMeta.title}</h3>
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            style={{ background: 'none', border: 'none', color: C.t3, cursor: 'pointer', padding: 4, fontSize: 18, lineHeight: 1 }}>
            ✕
          </button>
        </div>

        <div style={{ padding: '12px 16px', overflowY: 'auto', flex: 1 }}>
          <ExamSetupBody
            examType={examType} campaign={campaign} pool={pool} config={config} onConfigChange={onConfigChange}
            selectedKeys={selectedKeys} onToggleKey={onToggleKey} visiblePool={visiblePool} error={error}
            generating={generating} creating={creating} codingQuestionCount={codingQuestionCount}
            codingGenerateOptions={codingGenerateOptions} onGenerateJd={onGenerateJd} onGenerateEnglish={onGenerateEnglish}
            onCreate={onCreate} onClose={onClose} onCodingCountChange={onCodingCountChange} onCodingGenerateError={onCodingGenerateError}
          />
        </div>

        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          padding: '10px 16px', borderTop: `1px solid ${C.borderLight}`, flexShrink: 0,
        }}>
          <button type="button" onClick={onClose}
            style={{ fontSize: 12, color: C.t3, background: 'none', border: `1px solid ${C.border}`, padding: '7px 14px', borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit' }}>
            Cancel
          </button>
          <button
            type="button"
            onClick={onCreate}
            disabled={creating}
            style={{ fontSize: 12, fontWeight: 700, color: 'white', background: creating ? C.t4 : C.brand, border: 'none', padding: '7px 16px', borderRadius: 7, cursor: creating ? 'wait' : 'pointer', fontFamily: 'inherit' }}
          >
            {creating ? 'Creating…' : 'Create Exam'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** "+ Add Test" builder: pick exam type card → modal for config + questions → Create. */
/** State + handlers of the exam-set builder (config, question selection, generate, create).
 * `handleCreate` creates the exam set with today's rules and calls onCreated(examSet). */
export function useExamSetBuilder({ campaign, pool, onCreated, onPoolRefresh, existingExamCount = 0 }) {
  const [modalExamType, setModalExamType] = useState(null);
  const [config, setConfig] = useState(() => defaultAssessmentConfig(campaign));
  const [selectedKeys, setSelectedKeys] = useState(() => defaultSelectedKeys(pool, 'jd'));
  const [creating, setCreating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [codingQuestionCount, setCodingQuestionCount] = useState(0);
  const [deniedMode, setDeniedMode] = useState(null);

  const examType = modalExamType || 'jd';

  const { withIdx } = useMemo(() => groupQuestions(pool), [pool]);
  const keyToId = useMemo(() => {
    const m = new Map();
    withIdx.forEach((q) => { if (q.id != null) m.set(q._key, q.id); });
    return m;
  }, [withIdx]);
  const visiblePool = useMemo(() => filterPoolByExamType(pool, examType), [pool, examType]);

  useEffect(() => {
    if (modalExamType) setSelectedKeys(defaultSelectedKeys(pool, modalExamType));
  }, [pool, modalExamType]);

  const openModal = (modeId) => {
    if (!examModeAllowed(modeId)) {
      setDeniedMode(modeId);
      setModalExamType(null);
      return;
    }
    setDeniedMode(null);
    setModalExamType(modeId);
    setConfig(defaultAssessmentConfig(campaign));
    setError('');
    setSelectedKeys(defaultSelectedKeys(pool, modeId));
  };

  const closeModal = () => {
    setModalExamType(null);
    setDeniedMode(null);
    setError('');
  };

  const toggleKey = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const saveMergedQuestions = async (newRows, mode) => {
    const existingRes = await campaignAPI.listQuestions(campaign.id);
    const current = (existingRes.data?.questions || []).map(mapSavedQuestionRow);
    let merged;
    if (mode === 'jd') {
      merged = [...newRows, ...current.filter((q) => (q.exam_category || 'jd') !== 'jd')];
    } else if (mode === 'english') {
      merged = [...current.filter((q) => q.exam_category !== 'english'), ...newRows];
    } else {
      merged = [...current, ...newRows];
    }
    await campaignAPI.saveQuestions(campaign.id, merged);
    if (onPoolRefresh) await onPoolRefresh();
  };

  const handleGenerateJdQuestions = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await campaignAPI.previewQuestions({
        job_role: campaign.job_role || campaign.name,
        job_description: campaign.job_description,
        required_skills: campaign.required_skills || [],
        experience_years: campaign.experience_level
          ? parseInt(campaign.experience_level, 10) || undefined
          : undefined,
        num_questions: config.num_questions,
        difficulty: config.difficulty,
        question_type: config.question_type,
        language: config.language || 'en',
      });
      const rows = (res.data?.questions || []).map((q) => ({
        question: q.question_text || q.question || '',
        type: (q.question_type || config.question_type || 'technical').toLowerCase(),
        difficulty: (q.difficulty || config.difficulty || 'medium').toLowerCase(),
        exam_category: 'jd',
      }));
      await saveMergedQuestions(rows, 'jd');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate JD questions.');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateEnglishQuestions = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await campaignAPI.previewEnglishQuestions({
        job_role: campaign.job_role || campaign.name,
        job_description: campaign.job_description,
        required_skills: campaign.required_skills || [],
        experience_years: campaign.experience_level
          ? parseInt(campaign.experience_level, 10) || undefined
          : undefined,
        num_questions: config.num_questions,
        difficulty: config.difficulty,
        language: config.language || 'en',
      });
      const rows = (res.data?.questions || []).map((q) => ({
        question: q.question_text || '',
        type: 'english',
        difficulty: (q.difficulty || config.difficulty || 'medium').toLowerCase(),
        exam_category: 'english',
        skill_area: q.skill_area,
        language: config.language || 'en',
      }));
      await saveMergedQuestions(rows, 'english');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate proficiency questions.');
    } finally {
      setGenerating(false);
    }
  };

  const codingGenerateOptions = useMemo(() => ({
    num_questions: config.num_questions,
    difficulty: config.difficulty,
    grading_leniency: config.grading_leniency,
  }), [config]);

  const handleCreate = async () => {
    if (!modalExamType) return;
    let questionIds = [];
    if (modalExamType === 'coding') {
      if (codingQuestionCount === 0) { setError('Generate or add at least one coding question first.'); return; }
    } else if (modalExamType === 'interview_coding') {
      questionIds = [...selectedKeys].map((k) => keyToId.get(k)).filter(Boolean);
      if (questionIds.length === 0) { setError('Generate or select at least one AI interview question.'); return; }
      if (codingQuestionCount === 0) { setError('Generate or add at least one coding question first.'); return; }
    } else {
      questionIds = [...selectedKeys].map((k) => keyToId.get(k)).filter(Boolean);
      if (questionIds.length === 0) { setError('Generate or select at least one question.'); return; }
    }
    const modeTitle = EXAM_MODES.find((m) => m.id === modalExamType)?.title || 'Exam';
    const autoName = `${modeTitle} ${existingExamCount + 1}`;
    setCreating(true);
    setError('');
    try {
      const res = await examSetAPI.create(campaign.id, { name: autoName, exam_type: modalExamType, question_ids: questionIds });
      closeModal();
      onCreated(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create exam.');
    } finally {
      setCreating(false);
    }
  };

  return {
    modalExamType, openModal, closeModal, config, setConfig, selectedKeys, toggleKey, visiblePool,
    error, setError, generating, creating, codingQuestionCount, setCodingQuestionCount, codingGenerateOptions,
    handleGenerateJdQuestions, handleGenerateEnglishQuestions, handleCreate,
    // Exam mode the tenant isn't entitled to (demo: examModeAllowed) — shown as PermissionDeniedPanel.
    deniedMode,
  };
}

function ExamSetBuilder({ campaign, pool, onCancel, onCreated, onPoolRefresh, existingExamCount = 0 }) {
  const {
    modalExamType, openModal, closeModal, config, setConfig, selectedKeys, toggleKey, visiblePool,
    error, setError, generating, creating, codingQuestionCount, setCodingQuestionCount, codingGenerateOptions,
    handleGenerateJdQuestions, handleGenerateEnglishQuestions, handleCreate, deniedMode,
  } = useExamSetBuilder({ campaign, pool, onCreated, onPoolRefresh, existingExamCount });

  return (
    <div style={{ background: '#FFFFFF', border: `1.5px solid ${C.brand}`, borderRadius: 12, padding: 20, marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h4 style={{ fontSize: 15, fontWeight: 700, color: C.t1, margin: 0 }}>Create a New Test</h4>
        <button type="button" onClick={onCancel}
          style={{ fontSize: 12, color: C.t3, background: 'none', border: `1px solid ${C.border}`, padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' }}>
          Cancel
        </button>
      </div>

      <p style={{ fontSize: 12, color: C.t4, margin: '0 0 16px' }}>
        Choose an exam type to configure settings and questions.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${EXAM_MODES.length}, 1fr)`, gap: 10 }}>
        {EXAM_MODES.map((mode) => {
          const allowed = examModeAllowed(mode.id);
          return (
            <button
              key={mode.id}
              type="button"
              onClick={() => openModal(mode.id)}
              style={{
                textAlign: 'left', padding: '14px 16px', borderRadius: 10,
                border: `1.5px solid ${C.border}`,
                background: C.surface,
                cursor: 'pointer', fontFamily: 'inherit',
                transition: 'border-color .15s, background .15s',
                opacity: allowed ? 1 : 0.7,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.brand; e.currentTarget.style.background = C.brandLight; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.surface; }}
            >
              <mode.Icon size={20} color={C.brand} strokeWidth={1.6} />
              <div style={{ fontSize: 12, fontWeight: 700, color: C.t1, marginTop: 8 }}>{mode.title}</div>
            </button>
          );
        })}
      </div>

      {deniedMode ? <PermissionDeniedPanel /> : null}

      {modalExamType && (
        <ExamTypeSetupModal
          examType={modalExamType}
                campaign={campaign}
          pool={pool}
          config={config}
          onConfigChange={setConfig}
                selectedKeys={selectedKeys}
                onToggleKey={toggleKey}
          visiblePool={visiblePool}
          error={error}
          generating={generating}
          creating={creating}
          codingQuestionCount={codingQuestionCount}
          codingGenerateOptions={codingGenerateOptions}
          onGenerateJd={handleGenerateJdQuestions}
          onGenerateEnglish={handleGenerateEnglishQuestions}
          onCreate={handleCreate}
          onClose={closeModal}
          onCodingCountChange={setCodingQuestionCount}
          onCodingGenerateError={(msg) => setError(typeof msg === 'string' ? msg : 'Failed to generate coding questions.')}
        />
      )}
    </div>
  );
}

/** "Add Candidates" modal for one exam set: pick from this campaign's candidates,
 * or import not-yet-imported ReachOut synced candidates (assigns without emailing). */
function AddCandidatesModal({ campaign, examSet, onClose, onAssigned }) {
  const [showNew, setShowNew] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [syncedCandidates, setSyncedCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [selectedSynced, setSelectedSynced] = useState(new Set());
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [candidatesRes, syncedRes] = await Promise.all([
          campaignAPI.listCandidates(campaign.id),
          fetch(`${getPublicApiOrigin()}/api/integration/campaigns/${campaign.id}/synced-candidates`, {
            headers: localStorage.getItem('token') ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {},
          })
            .then((r) => (r.ok ? r.json() : { candidates: [] }))
            .catch(() => ({ candidates: [] })),
        ]);
        const rows = candidatesRes.data?.data || candidatesRes.data || [];
        const nativeList = Array.isArray(rows) ? rows : [];
        if (cancelled) return;
        setCandidates(nativeList);
        // Show every synced candidate linked to this position (not just
        // ReachOut-shortlisted ones) that isn't already imported here.
        const nativeEmails = new Set(nativeList.map((c) => (c.email || '').toLowerCase()));
        const syncedRows = (syncedRes.candidates || []).filter(
          (sc) => sc.email && !nativeEmails.has(sc.email.toLowerCase()),
        );
        setSyncedCandidates(syncedRows);
      } catch {
        if (!cancelled) { setCandidates([]); setSyncedCandidates([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [campaign.id]);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleSynced = (id) => setSelectedSynced((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const handleAssign = async () => {
    if (selected.size === 0 && selectedSynced.size === 0) return;
    setAssigning(true);
    setError('');
    try {
      const calls = [];
      if (selected.size > 0) calls.push(examSetAPI.assignCandidates(campaign.id, examSet.id, [...selected]));
      if (selectedSynced.size > 0) calls.push(examSetAPI.importSyncedCandidates(campaign.id, examSet.id, [...selectedSynced]));
      await Promise.all(calls);
      onAssigned();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to assign candidates.');
    } finally {
      setAssigning(false);
    }
  };

  const totalSelected = selected.size + selectedSynced.size;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'white', borderRadius: 14, width: 560, maxWidth: '92vw', maxHeight: '82vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.t1 }}>Add Candidates</div>
            <div style={{ fontSize: 12, color: C.t4 }}>{examSet.name}</div>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, color: C.t3, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ padding: '14px 20px', overflowY: 'auto', flex: 1 }}>
          {error && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '9px 12px', marginBottom: 12, fontSize: 12, color: '#991B1B' }}>
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={() => setShowNew(true)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%',
              fontSize: 12, fontWeight: 600, color: C.brand, background: C.brandLight,
              border: `1px dashed ${C.brand}`, padding: '10px 12px', borderRadius: 8,
              marginBottom: 14, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            + Add New Candidate (not on this campaign yet)
          </button>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: C.t4, fontSize: 13 }}>Loading candidates…</div>
          ) : (
            <>
              {candidates.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px 0', color: C.t4, fontSize: 13 }}>
                  No candidates on this campaign yet.
                </div>
              ) : (
                candidates.map((c) => {
                  const alreadyHere = c.exam_set_id === examSet.id;
                  const elsewhere = c.exam_set_id && c.exam_set_id !== examSet.id;
                  return (
                    <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 6px', borderBottom: `1px solid ${C.borderLight}`, cursor: alreadyHere ? 'default' : 'pointer' }}>
                      <input
                        type="checkbox"
                        disabled={alreadyHere}
                        checked={alreadyHere || selected.has(c.id)}
                        onChange={() => toggle(c.id)}
                        style={{ accentColor: C.brand, cursor: 'pointer' }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: C.t1 }}>{c.name}</div>
                        <div style={{ fontSize: 11, color: C.t4 }}>{c.email}</div>
                      </div>
                      {alreadyHere && (
                        <span style={{ fontSize: 10, fontWeight: 700, color: C.green, background: '#ECFDF5', padding: '2px 8px', borderRadius: 20 }}>✓ In this test</span>
                      )}
                      {elsewhere && (
                        <span
                          title={`Already assigned to ${c.exam_set_name || 'another test'}. Selecting them here adds a separate entry for this test with its own invite link — their other assignment is unaffected.`}
                          style={{ fontSize: 10, fontWeight: 600, color: C.amber, background: '#FFFBEB', padding: '2px 8px', borderRadius: 20 }}
                        >
                          Also in: {c.exam_set_name || 'another test'}
                        </span>
                      )}
                    </label>
                  );
                })
              )}

              {syncedCandidates.length > 0 && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.teal, textTransform: 'uppercase', letterSpacing: '.5px', margin: '18px 0 8px' }}>
                    From ReachOut — not yet imported ({syncedCandidates.length})
                  </div>
                  {syncedCandidates.map((sc) => (
                    <label key={`sc-${sc.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 6px', borderBottom: `1px solid ${C.borderLight}`, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={selectedSynced.has(sc.id)}
                        onChange={() => toggleSynced(sc.id)}
                        style={{ accentColor: C.teal, cursor: 'pointer' }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: C.t1 }}>{sc.name || sc.email}</div>
                        <div style={{ fontSize: 11, color: C.t4 }}>{sc.email}</div>
                      </div>
                      {sc.is_shortlisted ? (
                        <span style={{ fontSize: 10, fontWeight: 700, color: C.green, background: '#ECFDF5', padding: '2px 8px', borderRadius: 20 }}>★ Shortlisted</span>
                      ) : (
                        <span style={{ fontSize: 10, fontWeight: 600, color: C.t4, background: '#F3F4F6', padding: '2px 8px', borderRadius: 20 }}>Not shortlisted</span>
                      )}
                      <span style={{ fontSize: 10, fontWeight: 700, color: C.teal, background: C.tealLight, padding: '2px 8px', borderRadius: 20 }}>ReachOut</span>
                    </label>
                  ))}
                </>
              )}
            </>
          )}
        </div>

        <div style={{ padding: '14px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" onClick={onClose}
            style={{ fontSize: 13, color: C.t3, background: 'none', border: `1px solid ${C.border}`, padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' }}>
            Close
          </button>
          <button
            type="button"
            onClick={handleAssign}
            disabled={assigning || totalSelected === 0}
            style={{ fontSize: 13, fontWeight: 700, color: 'white', background: (assigning || totalSelected === 0) ? C.t4 : C.brand, border: 'none', padding: '8px 18px', borderRadius: 8, cursor: (assigning || totalSelected === 0) ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}
          >
            {assigning ? 'Assigning…' : `Assign (${totalSelected})`}
          </button>
        </div>
      </div>
      {showNew && (
        <AddCandidatesLauncher
          campaignId={campaign.id}
          examSetId={examSet.id}
          onClose={() => setShowNew(false)}
          onAdded={() => { onAssigned?.(); onClose(); }}
        />
      )}
    </div>
  );
}

/** "Open Test" detail modal: linked questions (read-only) + assigned candidates with bulk invite. */
function ExamSetDetailModal({ campaign, examSet, onClose, onOpenAddCandidates }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [questions, setQuestions] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [sendingBulk, setSendingBulk] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [toast, setToast] = useState('');
  const [sessionsByCandidateId, setSessionsByCandidateId] = useState({});
  const [viewingSession, setViewingSession] = useState(null); // session-list row

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [detailRes, candidatesRes, sessionsRes] = await Promise.all([
        examSetAPI.get(campaign.id, examSet.id),
        campaignAPI.listCandidates(campaign.id),
        // Match sessions to candidates by candidate_id, not session.exam_set_id —
        // a candidate may complete an interview and only get assigned to this
        // exam set afterward, in which case their session's own exam_set_id is
        // still NULL even though they now belong here.
        sessionAPI.getAllSessions({ campaign_id: campaign.id }),
      ]);
      setQuestions(detailRes.data?.questions || []);
      const rows = candidatesRes.data?.data || candidatesRes.data || [];
      const all = Array.isArray(rows) ? rows : [];
      setCandidates(all.filter((c) => c.exam_set_id === examSet.id));

      const sessions = sessionsRes.data || [];
      const byCandidate = {};
      sessions.forEach((s) => {
        if (!s.candidate_id) return;
        // Keep the most recent session per candidate (list is already sorted newest-first).
        if (!byCandidate[s.candidate_id]) byCandidate[s.candidate_id] = s;
      });
      setSessionsByCandidateId(byCandidate);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load test details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [campaign.id, examSet.id]);

  const eligibleIds = useMemo(
    () => candidates.filter((c) => (c.invite_status || 'pending').toLowerCase() === 'pending').map((c) => c.id),
    [candidates],
  );
  const allEligibleSelected = eligibleIds.length > 0 && eligibleIds.every((id) => selected.has(id));

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleAll = () => setSelected(allEligibleSelected ? new Set() : new Set(eligibleIds));

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const handleBulkSend = async () => {
    if (selected.size === 0) return;
    setSendingBulk(true);
    setError('');
    try {
      const data = await sendBulkInvite([...selected]);
      showToast(`Invite sent to ${data.sent_count ?? selected.size} candidate(s).`);
      setSelected(new Set());
      load();
    } catch (err) {
      setError(err.message || 'Failed to send invites.');
    } finally {
      setSendingBulk(false);
    }
  };

  const handleSingleSend = async (candidateId) => {
    setSendingId(candidateId);
    setError('');
    try {
      await sendSingleInvite(candidateId);
      showToast('Invite sent.');
      load();
    } catch (err) {
      setError(err.message || 'Failed to send invite.');
    } finally {
      setSendingId(null);
    }
  };

  const handleViewReport = (session) => {
    // Every candidate/session in this modal belongs to this named exam set —
    // the report component is picked below based on examSet.exam_type.
    setViewingSession(session);
  };

  const closeReport = () => {
    setViewingSession(null);
  };

  return (
    <>
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'white', borderRadius: 14, width: 720, maxWidth: '94vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.t1 }}>{examSet.name}</div>
            <div style={{ marginTop: 4 }}><ModeBadge examType={examSet.exam_type} /></div>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, color: C.t3, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ padding: '14px 20px', overflowY: 'auto', flex: 1 }}>
          {toast && (
            <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#065F46', fontWeight: 600 }}>
              ✓ {toast}
            </div>
          )}
          {error && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '9px 12px', marginBottom: 12, fontSize: 12, color: '#991B1B' }}>
              {error}
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: C.t4, fontSize: 13 }}>Loading…</div>
          ) : (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.t2, marginBottom: 8 }}>Questions ({questions.length})</div>
              <div style={{ marginBottom: 22 }}>
                {questions.length === 0 ? (
                  <div style={{ fontSize: 12, color: C.t4 }}>No questions linked to this test.</div>
                ) : (
                  <GroupedQuestionList mode="readonly" questions={questions} campaign={campaign} />
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.t2 }}>Candidates ({candidates.length})</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={onOpenAddCandidates}
                    style={{ fontSize: 11, fontWeight: 600, color: C.brand, background: C.brandLight, border: 'none', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' }}>
                    + Add Candidates
                  </button>
                  {eligibleIds.length > 0 && (
                    <button
                      type="button"
                      onClick={handleBulkSend}
                      disabled={sendingBulk || selected.size === 0}
                      style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: 'white', background: (sendingBulk || selected.size === 0) ? C.t4 : C.brand, border: 'none', padding: '6px 12px', borderRadius: 6, cursor: (sendingBulk || selected.size === 0) ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}
                    >
                      <MailIcon size={11} strokeWidth={2} color="white" />
                      {sendingBulk ? 'Sending…' : `Send Invite (${selected.size})`}
                    </button>
                  )}
                </div>
              </div>

              {candidates.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px 0', fontSize: 12, color: C.t4, border: `1px dashed ${C.border}`, borderRadius: 8 }}>
                  No candidates assigned to this test yet.
                </div>
              ) : (
                <div style={{ border: `1px solid ${C.borderLight}`, borderRadius: 8, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#F9FAFB' }}>
                        <th style={{ width: 34, padding: '8px 10px' }}>
                          {eligibleIds.length > 0 && (
                            <input type="checkbox" checked={allEligibleSelected} onChange={toggleAll} style={{ accentColor: C.brand, cursor: 'pointer' }} />
                          )}
                        </th>
                        <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, color: C.t4, fontWeight: 600 }}>Candidate</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, color: C.t4, fontWeight: 600 }}>Status</th>
                        <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 10, color: C.t4, fontWeight: 600 }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {candidates.map((c) => {
                        const st = (c.invite_status || 'pending').toLowerCase();
                        const ss = STATUS_STYLE[st] || STATUS_STYLE.pending;
                        const isEligible = st === 'pending';
                        return (
                          <tr key={c.id} style={{ borderTop: `1px solid ${C.borderLight}` }}>
                            <td style={{ padding: '8px 10px' }}>
                              {isEligible && (
                                <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} style={{ accentColor: C.brand, cursor: 'pointer' }} />
                              )}
                            </td>
                            <td style={{ padding: '8px 10px' }}>
                              <div style={{ fontSize: 12, fontWeight: 500, color: C.t1 }}>{c.name}</div>
                              <div style={{ fontSize: 11, color: C.t4 }}>{c.email}</div>
                            </td>
                            <td style={{ padding: '8px 10px' }}>
                              <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 9px', borderRadius: 20, background: ss.bg, color: ss.color }}>{ss.label}</span>
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                {sessionsByCandidateId[c.id]?.status === 'completed' && (
                                  <button
                                    type="button"
                                    onClick={() => handleViewReport(sessionsByCandidateId[c.id])}
                                    style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: C.green, background: 'none', border: `1px solid ${C.green}`, padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' }}
                                  >
                                    <BarChartIcon size={11} strokeWidth={2} color={C.green} />
                                    View Report
                                  </button>
                                )}
                                <button
                                  type="button"
                                  disabled={sendingId === c.id}
                                  onClick={() => handleSingleSend(c.id)}
                                  style={{ fontSize: 11, fontWeight: 600, color: C.brand, background: 'none', border: `1px solid ${C.brand}`, padding: '4px 10px', borderRadius: 6, cursor: sendingId === c.id ? 'wait' : 'pointer', fontFamily: 'inherit' }}
                                >
                                  {sendingId === c.id ? '…' : st === 'pending' ? 'Send Invite' : 'Resend'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>

    {viewingSession && <ReportModal sessionId={viewingSession.id} onClose={closeReport} />}
    </>
  );
}

function TileStat({ icon: StatIcon, value, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
      <StatIcon size={14} color={C.t4} strokeWidth={1.8} />
      <span style={{ fontSize: 13, fontWeight: 600, color: C.t1 }}>{value}</span>
      <span style={{ fontSize: 12, color: C.t4 }}>{label}</span>
    </div>
  );
}

function ExamSetTile({ examSet, onOpen, onAddCandidates, onDelete, onViewReports }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const qCount = examSet.question_count ?? 0;
  const cCount = examSet.candidate_count ?? 0;
  const doneCount = examSet.completed_count ?? 0;

  return (
    <div
      style={{
        background: '#FFFFFF', border: `1px solid ${C.border}`, borderRadius: 12,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        transition: 'box-shadow .15s, border-color .15s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 14px rgba(15,23,42,.06)'; e.currentTarget.style.borderColor = '#D7DAE2'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = C.border; }}
    >
      <div style={{ padding: '16px 18px 14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 16 }}>
          <ModeBadge examType={examSet.exam_type} />
          <div ref={menuRef} style={{ position: 'relative', flexShrink: 0 }}>
          <button
            type="button"
              onClick={() => setMenuOpen((v) => !v)}
              title="Options"
              aria-label="Exam options"
              style={{ background: 'none', border: 'none', color: C.t4, cursor: 'pointer', padding: 4, borderRadius: 6, display: 'flex' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#F3F4F6'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
            >
              <MoreIcon size={16} />
          </button>
            {menuOpen && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 20,
                background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8,
                boxShadow: '0 8px 24px rgba(15,23,42,.12)', minWidth: 140, overflow: 'hidden',
              }}>
          <button
                  type="button"
                  onClick={() => { setMenuOpen(false); onDelete(); }}
                  style={{
                    width: '100%', textAlign: 'left', fontSize: 12, fontWeight: 500, color: C.red,
                    background: 'none', border: 'none', padding: '10px 14px', cursor: 'pointer', fontFamily: 'inherit',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#FEF2F2'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                >
                  Delete exam
          </button>
        </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 20px', marginBottom: 4 }}>
          <TileStat icon={FileTextIcon} value={qCount} label={qCount === 1 ? 'Question' : 'Questions'} />
          <TileStat icon={UsersIcon} value={cCount} label={cCount === 1 ? 'Candidate' : 'Candidates'} />
          <TileStat icon={CheckCircleIcon} value={doneCount} label="Completed" />
        </div>
      </div>

      <div style={{ padding: '0 18px 16px', display: 'flex', flexDirection: 'column', gap: 8, marginTop: 'auto' }}>
        <button
          type="button"
          onClick={onOpen}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: 'white', background: C.brand, border: 'none', padding: '10px 12px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#4F46E5'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = C.brand; }}
        >
          Open Test
          <ArrowRightIcon size={13} strokeWidth={2.2} color="white" />
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={onAddCandidates}
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: C.brand, background: C.brandLight, border: 'none', padding: '8px 8px', borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit' }}>
            <UsersIcon size={12} strokeWidth={2} color={C.brand} />
            Add Candidates
          </button>
          <button type="button" onClick={onViewReports}
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: C.t2, background: '#fff', border: `1px solid ${C.border}`, padding: '8px 8px', borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit' }}>
            <BarChartIcon size={12} strokeWidth={2} color={C.t2} />
            Reports
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ExamTab({ campaign, savedQs, savedQsLoading, onGoToQuestions, onReloadQuestions }) {
  const navigate = useNavigate();
  const [examSets, setExamSets] = useState([]);
  const [loadingSets, setLoadingSets] = useState(true);
  const [setsError, setSetsError] = useState('');
  const [showBuilder, setShowBuilder] = useState(false);
  const [addCandidatesFor, setAddCandidatesFor] = useState(null); // examSet or null
  const [openDetailFor, setOpenDetailFor] = useState(null); // examSet or null
  const [detailReloadKey, setDetailReloadKey] = useState(0);
  const loadInFlight = useRef(false);

  const loadExamSets = async () => {
    if (loadInFlight.current) return;
    loadInFlight.current = true;
    setLoadingSets(true);
    setSetsError('');
    try {
      const res = await examSetAPI.list(campaign.id);
      setExamSets(res.data?.exam_sets || []);
    } catch (err) {
      setSetsError(err.response?.data?.detail || 'Failed to load exams.');
    } finally {
      setLoadingSets(false);
      loadInFlight.current = false;
    }
  };

  useEffect(() => { loadExamSets(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [campaign.id]);

  const handleDelete = async (examSet) => {
    if (!window.confirm(`Delete this ${EXAM_MODES.find((m) => m.id === examSet.exam_type)?.title || 'exam'}? This cannot be undone.`)) return;
    try {
      await examSetAPI.remove(campaign.id, examSet.id);
      loadExamSets();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Failed to delete exam.');
    }
  };

  const handleViewReports = (examSet) => {
    navigate(`/admin/reports?exam_set_id=${examSet.id}`);
  };

  return (
    <div style={{ paddingTop: 20 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: C.t1, margin: 0 }}>Exams</h3>
          <p style={{ fontSize: 12, color: C.t4, margin: '4px 0 0' }}>
            Create multiple tests for this position — pick a mode, choose questions, and assign different candidates to each.
          </p>
        </div>
        {!showBuilder && (
          <button
            type="button"
            onClick={() => setShowBuilder(true)}
            style={{ fontSize: 13, fontWeight: 700, color: 'white', background: C.brand, border: 'none', padding: '9px 18px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}
          >
            + Add Another Exam
          </button>
        )}
      </div>

      {setsError && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#991B1B' }}>
          {setsError}
        </div>
      )}

      {showBuilder && (
        <ExamSetBuilder
          campaign={campaign}
          pool={savedQsLoading ? [] : savedQs}
          existingExamCount={examSets.length}
          onCancel={() => setShowBuilder(false)}
          onCreated={() => { setShowBuilder(false); loadExamSets(); }}
          onPoolRefresh={onReloadQuestions}
        />
      )}

      {loadingSets ? (
        <div style={{ textAlign: 'center', padding: '48px 0', fontSize: 13, color: C.t4 }}>⏳ Loading exams…</div>
      ) : (
        <>
          {examSets.length === 0 && !showBuilder && (
        <div style={{ textAlign: 'center', padding: '48px 24px', background: '#FFFFFF', borderRadius: 12, border: `1px solid ${C.border}` }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: C.brandLight, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                <PlusIcon size={20} color={C.brand} strokeWidth={2} />
          </div>
              <h4 style={{ fontSize: 15, fontWeight: 600, color: C.t1, margin: '0 0 8px' }}>No exams yet</h4>
          <p style={{ fontSize: 13, color: C.t4, margin: '0 0 16px', maxWidth: 380, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.6 }}>
                Create an exam for this position.
              </p>
              <button
                type="button"
                onClick={() => setShowBuilder(true)}
                style={{ fontSize: 13, fontWeight: 700, color: C.brand, background: '#fff', border: `1px solid ${C.brand}`, padding: '9px 18px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                + Add Another Exam
            </button>
        </div>
          )}

          {examSets.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {examSets.map((es) => (
            <ExamSetTile
              key={es.id}
              examSet={es}
              onOpen={() => setOpenDetailFor(es)}
              onAddCandidates={() => setAddCandidatesFor(es)}
              onDelete={() => handleDelete(es)}
                  onViewReports={() => handleViewReports(es)}
            />
          ))}
        </div>
          )}
        </>
      )}

      {openDetailFor && (
        <ExamSetDetailModal
          key={detailReloadKey}
          campaign={campaign}
          examSet={openDetailFor}
          onClose={() => setOpenDetailFor(null)}
          onOpenAddCandidates={() => setAddCandidatesFor(openDetailFor)}
        />
      )}

      {addCandidatesFor && (
        <AddCandidatesModal
          campaign={campaign}
          examSet={addCandidatesFor}
          onClose={() => setAddCandidatesFor(null)}
          onAssigned={() => {
            setAddCandidatesFor(null);
            loadExamSets();
            setDetailReloadKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
}
